/** duplicate with common/src/utils */

import { isTelephone, isPhone, isBankCard, isEmail } from '@mudas/validate';
import dayjs from 'dayjs';
import { HelpValidateProps } from '@/types/validator';

export const CHINA_PHONE_REG = /^(1)[0-9]{10}$/;
export const phoneOrEmailReg = /^1\d{10}$|^\w+@(\w+\.)+com$/;

export const isPhoneNumberReg = /^1\d{10}$/;

export const isBank = /^.{0,30}$/;

export const isFixedLineReg = /^\d{7,12}/;

export const isEmailReg = /^[a-z0-9_.-]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z0-9\u4e00-\u9fa5]{2,6}$/i;

export const isNo = /^\d+$/;

export const desensitizePhoneNumberReg = /^\d{3}(\d{4})\d{4}$/;

export const desensitizeEmailReg = /^\w{3}(\w+)@\w+.com$/;

export const isImageUpload = /^image\//;

export const isNeteaseEmail = /^.*?@(163.com|126.com|vip.163.com|vip.126.com|188.com|yeah.net)$/;

export const isIntegerNum = /^[1-9]\d*$/;

export const isPositiveNum = /^\d+(\.\d{1,2})?$/;

export const isBankCardBeyond = /^[1-9]{1}(\d{8,22})$/;

// 判断50位以内的正整数（不包含小数和负数）
export const isOverseasBankCardBeyond = /^\d{1,50}$/;

export const isSynchronizePath = /^\/\/((?!\/)[\s\S]+\/?)([\s\S]+\/?)*$/;

export const NAME_REG = /^([\u4e00-\u9fa5]|[a-zA-Z0-9_-]){1,20}$/;

const compareCheckboxValue = (arr1: any[], arr2: any[]) => {
  if (!arr1 || !arr2) return false;
  let flag = true;
  if (arr1.length !== arr2.length) {
    flag = false;
  } else {
    arr1.forEach((item) => {
      if (!arr2.includes(item)) {
        flag = false;
      }
    });
  }
  return flag;
};

export const ruleMap = {
  stringRequired: { required: true, whitespace: true, message: '必填' },
  required: { required: true },
  email: {
    type: 'email' as 'email',
    message: '邮箱格式错误',
  },
  isNull: () => ({
    validator: (_: any, value: string) => {
      const regex = /^ *$/;
      return !regex.test(value) ? Promise.resolve() : Promise.reject('不支持仅输入空格字符');
    },
  }),
  isEmail: () => ({
    validator: (_: any, value: string) => (isEmail(value) ? Promise.resolve() : Promise.reject('邮箱格式不正确')),
  }),
  isBank: () => ({
    validator: (_: any, value: string) => {
      return isBank.test(value) ? Promise.resolve() : Promise.reject('最多支持输入30字符');
    },
  }),
  isNeteaseEmail: () => ({
    validator: (_: any, value: string) => {
      if (isEmail(value)) {
        return isNeteaseEmail.test(value) ? Promise.resolve() : Promise.reject('请输入网易邮箱');
      }
      return Promise.resolve();
    },
  }),
  isPhoneOrTel: () => ({
    validator: (_: any, value: string) =>
      isPhone(value) || isTelephone(value) ? Promise.resolve() : Promise.reject('请输入合法手机号或座机号'),
  }),
  isPhone: () => ({
    validator: (_: any, value: string) =>
      isPhone(value) ? Promise.resolve() : Promise.reject('请输入中国大陆境内合法手机号'),
  }),
  isBankCard: () => ({
    validator: (_: any, value: string) => (isBankCard(value) ? Promise.resolve() : Promise.reject('银行卡号不能为空')),
  }),
  isBankCardBeyond: () => ({
    validator: (_: any, value: string) =>
      isBankCardBeyond.test(value) ? Promise.resolve() : Promise.reject('银行卡号是9-23位数字,请重新输入'),
  }),
  isSplitByEnComma: () => ({
    validateTrigger: 'onBlur',
    validator: (_: any, value: string) =>
      value.includes('，') ? Promise.reject(new Error('请使用英文逗号隔开')) : Promise.resolve(),
  }),
  tags: (text: string, min: number = 2) => ({
    validateTrigger: 'onChange',
    validator: (_: any, value: string[]) => {
      if (value.some((item) => !item || !item.trim())) return Promise.reject(new Error('不可输入空字符串或纯空格'));
      if (value.length !== Array.from(new Set(value)).length) return Promise.reject(new Error(`不可输入重复的${text}`));
      if (Array.from(new Set(value)).length < min) return Promise.reject(new Error(`至少输入${min}个${text}`));
      return Promise.resolve();
    },
  }),
  scoreDimensions: () => ({
    validateTrigger: 'onBlur',
    validator: (_: any, value: string[]) => {
      if (value.some((item) => !item || !item.trim())) return Promise.reject(new Error('不可输入空字符串或纯空格'));
      if (value.length !== Array.from(new Set(value)).length) return Promise.reject(new Error('不可输入重复值'));
      return Promise.resolve();
    },
  }),
  frameSelectTags: () => ({
    validateTrigger: 'onBlur',
    validator: (_: any, value: string[]) => {
      if (value.some((item) => !item || !item.trim())) return Promise.reject(new Error('不可输入空字符串或纯空格'));
      if (value.length !== Array.from(new Set(value)).length) return Promise.reject(new Error(`不可输入重复的标签`));
      return Promise.resolve();
    },
  }),
  notSameValue: (originValue: string[]) => ({
    validateTrigger: 'onChange',
    validator: (_: any, value: string[]) => {
      if (compareCheckboxValue(originValue, value)) {
        return Promise.reject(new Error('不可输入相同答案'));
      }
      return Promise.resolve();
    },
  }),
  validateTimeRangeRule: (disabled: boolean) => ({
    validateTrigger: 'onChange',
    validator: (_: any, { startTime, endTime }: { startTime?: dayjs.Dayjs; endTime?: dayjs.Dayjs }) => {
      const now = dayjs();
      // disbaled=true时，为冻结状态下重新编辑 不需要校验startTm
      const startTm = !disabled && startTime ? dayjs(startTime) : undefined;
      const endTm = endTime ? dayjs(endTime) : undefined;

      if (startTm && startTm < now) return Promise.reject('开始时间不能小于当前时刻');

      if (endTm) {
        if (endTm < (startTm || now)) {
          return Promise.reject('结束时间必须大于当前时间或开始时间');
        }
        if (!disabled && endTm.subtract(1, 'hour') < (startTm || now)) {
          return Promise.reject('结束时间与开始时间或当前时间间隔须大于1小时');
        }
      }

      return Promise.resolve();
    },
  }),
  isIntegerNum: () => ({
    validator: (_: any, value: string) =>
      isIntegerNum.test(value) ? Promise.resolve() : Promise.reject('请输入正整数'),
  }),
  isPositiveNum: () => ({
    validator: (_: any, value: string) =>
      isPositiveNum.test(value) ? Promise.resolve() : Promise.reject('请输入正数,且最多支持两位小数'),
  }),
  isSynchronizePath: () => ({
    validator: (_: any, value: string) =>
      isSynchronizePath.test(value) ? Promise.resolve() : Promise.reject('没有权限的路径，请核对后重新同步'),
  }),
  isMaxLength: (max: number, errorMsg?: string) => ({
    validator: (_: any, value: string) =>
      value.length <= max ? Promise.resolve() : Promise.reject(errorMsg || `最多支持${max}个字符`),
  }),
  isRangeLength: (min: number, max: number, errorMsg?: string) => ({
    validator: (_: any, value: string) =>
      value.length >= min && value.length <= max
        ? Promise.resolve()
        : Promise.reject(errorMsg || `请输入${min}-${max}个字符`),
  }),
};

export const getHelpValidateResult = (config): HelpValidateProps => {
  const { value = '', reg, attrName, errorMsg } = config;

  if (!value || value === '') {
    return {
      value,
      validateStatus: 'error',
      errorMsg: `${attrName}不能为空`,
    };
  }

  if (!reg.test(value)) {
    return {
      value,
      validateStatus: 'error',
      errorMsg,
    };
  }

  return {
    value,
    validateStatus: 'success',
    errorMsg: null,
  };
};
